import { Spin } from "antd";
import type { LineConfig } from "./LineChartCard";
import styles from "./SummaryComparisonTableCard.module.css";

export interface SummaryComparisonTableCardProps {
  title: string;
  data: Record<string, string | number>[];
  xKey: string;
  series: LineConfig[];
  loading?: boolean;
  error?: string | null;
  headerExtra?: React.ReactNode;
  valueSuffix?: string;
}

interface SummaryRow {
  key: string;
  label: string;
  color: string;
  values: number[];
  firstValue: number;
  lastValue: number;
  averageValue: number;
  delta: number;
}

const getNumericValue = (value: string | number | undefined) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatValue = (value: number, suffix: string) => {
  const formattedValue = Number.isInteger(value)
    ? value.toString()
    : value.toFixed(1).replace(/\.0$/, "");

  return `${formattedValue}${suffix}`;
};

const formatDelta = (value: number) => {
  const sign = value > 0 ? "+" : "";
  const formattedValue = Math.abs(value) < 0.05
    ? "0"
    : value.toFixed(1).replace(/\.0$/, "");

  return `${sign}${formattedValue} pp`;
};

const buildSparklinePoints = (values: number[], width: number, height: number) => {
  if (values.length === 0) return "";
  if (values.length === 1) return `${width / 2},${height / 2}`;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  const horizontalStep = width / (values.length - 1);
  const verticalPadding = 5;

  return values
    .map((value, index) => {
      const x = index * horizontalStep;
      const ratio = range === 0 ? 0.5 : (value - min) / range;
      const y = height - verticalPadding - ratio * (height - verticalPadding * 2);

      return `${x},${y}`;
    })
    .join(" ");
};

const buildRows = (
  data: Record<string, string | number>[],
  series: LineConfig[]
): SummaryRow[] => {
  return series
    .map(({ key, label, color }) => {
      const values = data
        .map((point) => getNumericValue(point[key]))
        .filter((value): value is number => value !== null);

      if (values.length === 0) return null;

      const firstValue = values[0];
      const lastValue = values[values.length - 1];
      const averageValue = values.reduce((total, value) => total + value, 0) / values.length;

      return {
        key,
        label,
        color,
        values,
        firstValue,
        lastValue,
        averageValue,
        delta: lastValue - firstValue,
      };
    })
    .filter((row): row is SummaryRow => row !== null)
    .sort((a, b) => b.lastValue - a.lastValue);
};

const SummaryComparisonTableCard = ({
  title,
  data,
  xKey,
  series,
  loading = false,
  error = null,
  headerExtra,
  valueSuffix = "%",
}: SummaryComparisonTableCardProps) => {
  const rows = buildRows(data, series);
  const firstPeriod = data[0]?.[xKey];
  const lastPeriod = data[data.length - 1]?.[xKey];
  const maxLastValue = rows.length > 0 ? Math.max(...rows.map((row) => row.lastValue)) : 0;

  return (
    <div className={styles.card} data-chart-card>
      <div className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
        {headerExtra}
      </div>

      {loading && (
        <div className={styles.centered}>
          <Spin size="small" /> Carregant dades…
        </div>
      )}
      {!loading && error && (
        <div className={styles.centered}>
          <p className={styles.errorMessage}>{error}</p>
        </div>
      )}
      {!loading && !error && rows.length === 0 && (
        <div className={styles.centered}>
          <p className={styles.emptyMessage}>Sense dades disponibles</p>
        </div>
      )}
      {!loading && !error && rows.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Àmbit</th>
                <th>{firstPeriod ?? "Inicial"}</th>
                <th>{lastPeriod ?? "Final"}</th>
                <th>Canvi</th>
                <th>Mitjana</th>
                <th>Tendència</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const deltaClass = row.delta > 0.05
                  ? styles.deltaUp
                  : row.delta < -0.05
                    ? styles.deltaDown
                    : styles.deltaFlat;
                const barWidth = maxLastValue > 0
                  ? Math.max(3, (row.lastValue / maxLastValue) * 100)
                  : 0;
                const sparklinePoints = buildSparklinePoints(row.values, 120, 34);
                const firstPoint = sparklinePoints.split(" ")[0];
                const lastPoint = sparklinePoints.split(" ").slice(-1)[0];

                return (
                  <tr key={row.key}>
                    <td className={styles.scopeCell}>
                      <div className={styles.scopeName}>
                        <span
                          className={styles.scopeDot}
                          style={{ backgroundColor: row.color }}
                        />
                        <span>{row.label}</span>
                      </div>
                    </td>
                    <td className={styles.valueCell}>
                      {formatValue(row.firstValue, valueSuffix)}
                    </td>
                    <td className={styles.finalCell}>
                      <div className={styles.finalValue}>
                        <span>{formatValue(row.lastValue, valueSuffix)}</span>
                        <span className={styles.valueBarTrack} aria-hidden="true">
                          <span
                            className={styles.valueBar}
                            style={{ width: `${barWidth}%`, backgroundColor: row.color }}
                          />
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`${styles.deltaBadge} ${deltaClass}`}>
                        {formatDelta(row.delta)}
                      </span>
                    </td>
                    <td className={styles.valueCell}>
                      {formatValue(row.averageValue, valueSuffix)}
                    </td>
                    <td className={styles.trendCell}>
                      <svg
                        className={styles.sparkline}
                        viewBox="0 0 120 34"
                        role="img"
                        aria-label={`${row.label}: ${formatValue(row.firstValue, valueSuffix)} a ${formatValue(row.lastValue, valueSuffix)}`}
                      >
                        <line className={styles.sparklineGuide} x1="0" y1="17" x2="120" y2="17" />
                        <polyline
                          className={styles.sparklineLine}
                          points={sparklinePoints}
                          stroke={row.color}
                        />
                        {firstPoint && (
                          <circle
                            className={styles.sparklineDot}
                            cx={firstPoint.split(",")[0]}
                            cy={firstPoint.split(",")[1]}
                            r="3"
                            fill={row.color}
                          />
                        )}
                        {lastPoint && (
                          <circle
                            className={styles.sparklineDot}
                            cx={lastPoint.split(",")[0]}
                            cy={lastPoint.split(",")[1]}
                            r="3"
                            fill={row.color}
                          />
                        )}
                      </svg>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SummaryComparisonTableCard;
